<template>
	<div class="row centered" v-show="showTable">
		<AppTable ref="credit-topups-table"
			v-if="userStore.currBusinessId"
			:fetch-function="wrappedFetch"
			:filters="filters"
			:table-headings="tableHeadings"
			:title="getString('customers_credit_topups')"
			:total-items="administrationStore.businessCreditTopupsTotal || 0"
			:items="topups"
			initial-sort-field="date"
			allow-download
			@export="exportTopups"
			@failed-fetch="details => { $emit('failed-fetch', details) }">
			<template #custom-header>
				<div class="header-title-container">
					<h3>{{ getString('customers_credit_topups') }}</h3>
				</div>
				<div class="header-actions">
					<AppSwitch left-icon="iconPerson" right-icon="iconCompany" fixed-color @switched="switchCustomerType" />
					<AppButtonIcon prevent-change color="var(--color-bg-primary-1)" icon-name="icon-download-blue" @click="toggleExportType" />
				</div>
			</template>
		</AppTable>
	</div>
</template>

<script>
import AppTable from "@/shared/components/AppTable.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import {useUserStore, useAdministrationStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";

export default {
	setup() {
		const userStore = useUserStore();
		const administrationStore = useAdministrationStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		return {userStore, administrationStore, currencyStore, getString: languageStore.getString, showNotification: notificationStore.showNotification};
	},
	name: "CreditTopupsTable",
	emits: ["failed-fetch"],
	components: {AppTable, AppSwitch, AppButtonIcon},
	data() {
		return {
			showTable: false,
			customerType: 'person',
			filters: [
				{
					type: "date",
					key: 'date',
					tableName: 'customers_balances_transactions',
					text: this.getString('date'),
					finalValue: '',
					active: false
				},
				{
					type: "price",
					key: "amount",
					text: this.getString('amount'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
			],
			tableHeadings: [
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					fieldName: 'date',
				},
				{
					text: this.getString('customer_name'),
					fieldName: 'customer_name',
					cols: 2,
				},
				{
					text: this.getString('amount'),
					cols: 1,
					sortable: true,
					fieldName: 'amount',
					formatter: 'currency',
				},
				{
					text: this.getString('payment'),
					fieldName: 'type',
					cols: 1,
				},
			],
		}
	},
	methods: {
		switchCustomerType(isRight) {
			this.customerType = isRight ? 'company' : 'person';
			this.$refs['credit-topups-table'].fetchData();
		},
		wrappedFetch(filters, callback) {
			filters.customer_type = this.customerType;
			this.administrationStore.getFilteredCreditTopups(filters, (success, details) => {
				if(this.administrationStore?.businessCreditTopups?.length) {
					this.showTable = true;
				}
				if (callback) { callback(success, details); }
			});
		},
		triggerResize() {
			this.$nextTick(() => {
				if (this.$refs['credit-topups-table']) {
					this.$refs['credit-topups-table'].initTableWidth();
				}
			});
		},
		toggleExportType() {
			this.$refs['credit-topups-table'].toggleExportType();
		},
		exportTopups(expExtension) {
			let sym = this.currencyStore.currCurrency.symbol;
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let today = new Date().toLocaleString();
			let fileName = businessNameNorm + "_" + this.getString('customers_credit_topups').toLowerCase().replace(/\s+/g, "_") + "_" + today.replace(", ", "_");
			exportToFile({
				columns: [
					{header: this.getString('date'), value: t => formatDateTimeWithSeconds(t.date, false)},
					{header: this.getString('customer_name'), key: 'customer_name'},
					{header: this.getString('amount') + " " + sym, value: t => this.currencyStore.dbToActualValue(t.amount), type: 'number', numberFormat: '#,##0.00'},
					{header: this.getString('payment'), key: 'type'},
				],
				rows: (this.topups || []),
				fileName,
				sheetName: this.getString("customers_credit_topups"),
				format: expExtension,
				onEmpty: () => this.showNotification('table-no-data'),
			});
		},
	},
	computed: {
		topups() {
			if (!this.administrationStore.businessCreditTopups || !this.administrationStore.businessCreditTopups.length) { return []; }
			return this.administrationStore.businessCreditTopups.map(t => {
				return {
					...t,
					type: t.type ? this.getString(t.type) : '-',
				};
			});
		},
	},
	watch: {
		showTable(newVal) {
			if (newVal) {
				this.triggerResize();
			}
		},
		'userStore.currBusinessId': {
			handler(newVal, oldVal) {
				if (newVal && newVal !== oldVal) {
					this.showTable = false;
					if(this.$refs?.['credit-topups-table']?.fetchData) {
						this.$refs['credit-topups-table'].fetchData();
					}
				}
			},
		},
	}
}
</script>

<style lang="scss" scoped>
.header-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
}
</style>